/**
 * Module dependencies.
 */

import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { useMutation } from '@tanstack/react-query';
import { message } from 'antd';
import type { ExportFormat, ExportQueueResponse } from 'app/types/export';

/**
 * `UseDataExportOptions` type.
 */

export interface UseDataExportOptions {
  resource: string;
  queryParams?: Record<string, any>;
  onSuccess?: (exportId: string) => void;
  onError?: (error: Error) => void;
}

/**
 * `ExportPayload` type.
 */

interface ExportPayload {
  format: ExportFormat;
  filename?: string;
}

/**
 * Export data function.
 */

async function queueExport(
  client: any,
  resource: string,
  payload: ExportPayload,
  queryParams?: Record<string, any>
): Promise<ExportQueueResponse> {
  const url = resolveAppUrl('resourceExport', {
    interpolations: { resource },
    params: queryParams
  });

  const response = await client.post(url, payload);

  return response.data?.data;
}

/**
 * Export `useDataExport` hook.
 */

export const useDataExport = (options: UseDataExportOptions) => {
  const { resource, queryParams, onSuccess, onError } = options;
  const client = useRequestProvider();

  const mutation = useMutation({
    mutationFn: (payload: ExportPayload) => queueExport(client, resource, payload, queryParams),
    onSuccess: data => {
      console.log('Export success! Data:', data);
      message.success('Export queued successfully! Check Settings → Data Exports to download.');
      onSuccess?.(data.exportId);
    },
    onError: error => {
      console.error('Export error:', error);
      message.error('Failed to queue export. Please try again.');
      onError?.(error as Error);
    }
  });

  return {
    exportData: (format: ExportFormat, filename?: string) => mutation.mutate({ format, filename }),
    isExporting: mutation.isLoading
  };
};
